// src/sim/dodge.ts — Dodge (yerde, 4 yön klibi, kök hareket ÖLÇEKLE 3.0 u lungeAxis üzerinden), hava dodge (darbe 4.5 u/s,
// yerçekimi ×0.3 18 t, 15 t saldırı yasağı), i-frame 2–12 t (11 t) + ardışık ceza, kenarda durur (collision BRAKE_CLIP); yuvarlanma knockdown.ts'te.
// Kök hareket algoritması ARCHITECTURE §4.6 / ANIMATION §5 ile aynı (zaman tabanı notu stepDodgeMotion'da); çıktı f.rmDx/rmDz (pos'a değil).
// three'siz, tahsissiz. Tahsis disiplini (ölçüm): yalnız seyrek durumlarda çalışan ayrı fonksiyonlar V8'de Maglev kademesinde kalıp
// double ara değerleri kutular → dodge başlangıcı (yön + eksen seçimi) tryDodge gövdesinde elle inline; kök hareket adımı stepLocomotion içinde.
import { Btn, Ev, FState } from '../core/types.ts';
import type { Fighter, InputFrame, SimWorld } from '../core/types.ts';
import { CLIP_FPS, NO_TICK, TICK_DT } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { CLIPS } from '../config/clipTable.ts';
import { bufferedPress, consumePress, setState } from './fighter.ts';

/** Dodge klip ekseni (Ev.Dodge v0; animMap aynı sırayla klip seçebilir). */
export const DODGE_AXIS_F = 0, DODGE_AXIS_B = 1, DODGE_AXIS_L = 2, DODGE_AXIS_R = 3;
const AXIS_CLIP: readonly string[] = [CLIPS.DODGE_F, CLIPS.DODGE_B, CLIPS.DODGE_L, CLIPS.DODGE_R];
const WALK_DEAD = T.MOVE.walkDead;

/** f.dodgeDirX/Z (dünya) → karaktere göre en yakın 4 eksen: |fwd| ≥ |side| → F/B, değilse L(+side)/R. Rig: ileri (sin, cos), sol (cos, −sin).
 *  Soğuk yol sürümü (animMap/bot); tryDodge aynı formülü yerinde hesaplar. */
export function dodgeAxisFor(f: Fighter): number {
  const s = Math.sin(f.yaw), c = Math.cos(f.yaw);
  const fwd = f.dodgeDirX * s + f.dodgeDirZ * c;
  const side = f.dodgeDirX * c - f.dodgeDirZ * s;
  const af = fwd < 0 ? -fwd : fwd, as = side < 0 ? -side : side;
  if (af >= as) return fwd >= 0 ? DODGE_AXIS_F : DODGE_AXIS_B;
  return side > 0 ? DODGE_AXIS_L : DODGE_AXIS_R;
}

/** Yerde dodge'a girilebilecek durumlar (hamle içi iptal penceresi kararı stepMoves'ta — çağıran karar verir). */
function groundDodgeOk(f: Fighter): boolean {
  const s = f.state;
  return s === FState.Idle || s === FState.Move || s === FState.Block || s === FState.Attack || s === FState.Taunt;
}

/**
 * SimSystems.tryDodge: tamponda R1 (9 t) varsa ve durum uygunsa dodge/hava dodge başlatır; true → basış tüketildi.
 * stepMoves (WP3) DESIGN §2 öncelik sırasında her tick çağırır (sıcak → TurboFan); WP2 testleri stub stepMoves ile çağırır.
 * Yön: çubuk (ölü bölge üstü) ya da nötr = geri (DESIGN §3). Ardışık dodge: 48 t içinde tekrar → streak++, i-frame = max(3, 11 − 3·streak).
 */
export function tryDodge(world: SimWorld, f: Fighter, input: InputFrame): boolean {
  const tick = world.tick;
  if (!bufferedPress(world, f, Btn.Dodge, T.INPUT.bufferTicks)) return false;
  const ground = f.grounded;
  if (ground) { if (!groundDodgeOk(f)) return false; }
  else {
    // hava dodge: 1×/hava; Air ya da tumble kontrolü dönmüş Tumble
    const airOk = f.state === FState.Air || (f.state === FState.Tumble && f.tumbleControl);
    if (!airOk || f.airDodgeLeft <= 0) return false;
  }
  consumePress(world, f, Btn.Dodge);
  // yön (yerinde): çubuk ya da geri
  const mx = input.moveX, mz = input.moveZ;
  const mag = Math.sqrt(mx * mx + mz * mz);
  const s = Math.sin(f.yaw), c = Math.cos(f.yaw);
  let dirX: number, dirZ: number;
  if (mag > WALK_DEAD) { dirX = mx / mag; dirZ = mz / mag; } else { dirX = -s; dirZ = -c; }
  f.dodgeDirX = dirX; f.dodgeDirZ = dirZ;
  // eksen (yerinde): |fwd| ≥ |side| → F/B, değilse L/R
  const fwd = dirX * s + dirZ * c, side = dirX * c - dirZ * s;
  const af = fwd < 0 ? -fwd : fwd, as = side < 0 ? -side : side;
  const axis = af >= as ? (fwd >= 0 ? DODGE_AXIS_F : DODGE_AXIS_B) : (side > 0 ? DODGE_AXIS_L : DODGE_AXIS_R);
  f.dodgeClip = world.clips.indexOf(AXIS_CLIP[axis]!);
  // ardışık sayaç
  if (f.lastDodgeTick !== NO_TICK && tick - f.lastDodgeTick < T.DODGE.streakResetTicks) f.dodgeStreak++;
  else f.dodgeStreak = 0;
  f.lastDodgeTick = tick;
  f.moveIdx = -1; f.phase = 0;                     // hamle iptal edildiyse hamle bilgisi temiz (WP3 kendi alanlarını da sıfırlar)
  f.invulnUntil = NO_TICK;                         // 2. t'de stepLocomotion (dodge kök hareketi) kurar
  if (ground) {
    f.blocking = false;
    f.vel.x = 0; f.vel.z = 0;                       // hareket yalnız kök hareketten (klip ivme profili)
    setState(f, FState.Dodge, tick, tick + T.DODGE.ticks);   // 19 t: T..T+18 Dodge, T+19 Idle
    const e = world.events.push(Ev.Dodge, tick, f.id);
    e.dx = dirX; e.dz = dirZ; e.v0 = axis;
    return true;
  }
  f.airDodgeLeft--;
  f.tumbleControl = false;
  // darbe: yatay 4.5 u/s çubuk yönünde; dikey bileşen ≤ +2 (düşüş durdurulur, yükseliş 2'ye kırpılır — WP2 yorumu)
  f.vel.x = dirX * T.DODGE.air.speed; f.vel.z = dirZ * T.DODGE.air.speed;
  f.vel.y = f.vel.y < 0 ? 0 : f.vel.y > T.DODGE.air.maxVy ? T.DODGE.air.maxVy : f.vel.y;
  f.fastFall = false; f.jumpHeld = false;
  f.airDodgeGravUntil = tick + T.DODGE.air.gravTicks;
  f.airDodgeNoAttackUntil = tick + T.DODGE.air.gravTicks + T.DODGE.air.noAttackTicks;
  setState(f, FState.AirDodge, tick, tick + T.DODGE.air.gravTicks);   // 18 t'de JumpAir
  const e = world.events.push(Ev.AirDodge, tick, f.id);
  e.dx = dirX; e.dz = dirZ; e.v0 = axis;
  return true;
}

// Yuvarlanma (Lying'de R1): TEK kaynak knockdown.ts `startRoll` (lyingInputs çağırır; stateUntil = tick + 21 + 1, i-frame tamamı).
// Buradaki eski kopya kaldırıldı (iki sürüm farklı bitiş kullanıyordu); hareket adımı ROLL_STEP ile fighter.ts'te.

// ── Kök hareket sabitleri (stepLocomotion içinde yerinde uygulanır; bkz. fighter.ts "dodge / roll kök hareketi") ──
export const ROLL_STEP = T.KNOCKDOWN.rollDist / T.KNOCKDOWN.rollTicks;
export const DODGE_LINEAR_STEP = T.DODGE.distance / T.DODGE.ticks;
export const RM_T0 = T.DODGE.rmWindow[0], RM_T1 = T.DODGE.rmWindow[1];
export const DODGE_DT_RATE = TICK_DT * T.DODGE.rate;
// Kare indeksi: WP1 frameOf sözleşmesi round(t·CLIP_FPS), [0, frames−1] clamp — clips.frameOf çağrısı yerine yerinde hesaplanır.

/**
 * Referans uygulama (soğuk yol / WP3 kök hareket için örnek; stepLocomotion aynı mantığı yerinde çalıştırır):
 * ÖLÇEKLE (ARCHITECTURE §4.6): s(f) = lungeFwd·ax + lungeLeft·az; ds = (s(fb) − s(fa))·distance / (s(t1) − s(t0));
 * tPrev/tNow pencereye clamp'lenir. Klip verisi yoksa (dodgeClip < 0) doğrusal yedek: distance / ticks.
 * ZAMAN TABANI (WP2 kararı): durum tick'i k için delta klip [k, k+1] karesinden alınır → durum 19 t = 19 hareket tick'i; görsel klip
 * zamanına 1 t önden (ANIMATION §5'teki tPrev = (k−1)·DT·tS ile başlangıç tick'i boş kalır ve durum 20 t sürerdi). Deviations'ta.
 */
export function dodgeRootMotion(world: SimWorld, f: Fighter, k: number): void {
  if (k < 0 || k >= T.DODGE.ticks) return;
  if (f.dodgeClip < 0) { f.rmDx += f.dodgeDirX * DODGE_LINEAR_STEP; f.rmDz += f.dodgeDirZ * DODGE_LINEAR_STEP; return; }
  const tPrev = k * DODGE_DT_RATE, tNow = (k + 1) * DODGE_DT_RATE;   // clipStart 0
  if (!(tNow > RM_T0 && tPrev < RM_T1)) return;
  const cd = world.clips.byIndex(f.dodgeClip);
  const frames = cd.frames, last = frames - 1;
  let fa = Math.round((tPrev < RM_T0 ? RM_T0 : tPrev > RM_T1 ? RM_T1 : tPrev) * CLIP_FPS); if (fa > last) fa = last;
  let fb = Math.round((tNow < RM_T0 ? RM_T0 : tNow > RM_T1 ? RM_T1 : tNow) * CLIP_FPS); if (fb > last) fb = last;
  if (fa === fb) return;
  let fs = Math.round(RM_T0 * CLIP_FPS); if (fs > last) fs = last;
  let fe = Math.round(RM_T1 * CLIP_FPS); if (fe > last) fe = last;
  const ax = cd.lungeAxis[0], az = cd.lungeAxis[1];
  const lf = cd.lungeFwd, ll = cd.lungeLeft;
  const den = (lf[fe]! * ax + ll[fe]! * az) - (lf[fs]! * ax + ll[fs]! * az);
  if (den < 1e-6 && den > -1e-6) return;   // build-clipdata > 0.05 u doğrular; savunma
  const ds = ((lf[fb]! * ax + ll[fb]! * az) - (lf[fa]! * ax + ll[fa]! * az)) * T.DODGE.distance / den;
  f.rmDx += f.dodgeDirX * ds; f.rmDz += f.dodgeDirZ * ds;
}

/** Dodge'un hafif saldırıyla iptal edilebileceği tick (DESIGN §3: 12 t'den sonra) — stepMoves sorar. */
export const dodgeLightCancelOpen = (f: Fighter, tick: number): boolean =>
  f.state === FState.Dodge && tick - f.stateTick >= T.DODGE.lightCancelAt;
